/**
 * HeroSectionBlock Component
 *
 * Custom BlockNote React component for hero sections with flexible layouts
 * and media options for marketing pages.
 *
 * Features:
 * - Small title (optional category/context)
 * - Main title (required)
 * - Description (required)
 * - Detail field (optional extended text)
 * - CTA buttons (1-2 max) with primary/secondary styles
 * - Featured image/video with upload support
 * - Background image/video with overlay controls
 * - 4 layout variations:
 *   - text-left: Text left / Image right
 *   - text-right: Text right / Image left
 *   - centered-bg: Centered text with background image
 *   - text-above: Text above image
 * - Edit mode with collapsible sections
 * - View mode with responsive rendering
 *
 * Props (from block.props):
 * - smallTitle: Optional category text (max 100 chars)
 * - title: Main headline (required, max 200 chars)
 * - description: Supporting text (required, max 500 chars)
 * - detail: Extended information (optional, rich text)
 * - buttons: Array of CTA buttons [{ text, url, style }] (max 2)
 * - featuredMedia: { type, url, alt } for main image/video
 * - backgroundMedia: { type, url, overlay } for background
 * - layout: "text-left"|"text-right"|"centered-bg"|"text-above"
 *
 * Related Files:
 * - app/components/blocknote/blocks/hero_section_component.rb (SSR)
 * - app/javascript/components/blocknote/marketing/shared/*.jsx
 */

import React, { useState } from "react";
import MediaSelector from "./marketing/shared/MediaSelector.jsx";
import LayoutSelector from "./marketing/shared/LayoutSelector.jsx";
import OverlayControls from "./marketing/shared/OverlayControls.jsx";
import ButtonEditor from "./marketing/shared/ButtonEditor.jsx";

// Layout options for Hero Section
const LAYOUT_OPTIONS = [
  {
    value: "text-left",
    label: "Text Left",
    description: "Text left, image right",
  },
  {
    value: "text-right",
    label: "Text Right",
    description: "Image left, text right",
  },
  {
    value: "centered-bg",
    label: "Centered Background",
    description: "Text over background",
  },
  {
    value: "text-above",
    label: "Text Above",
    description: "Text above image",
  },
];

export default function HeroSectionBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    content: true,
    media: true,
    background: false,
    layout: true,
  });

  // Get current props with defaults
  const props = block.props || {};
  const smallTitle = props.smallTitle || "";
  const title = props.title || "";
  const description = props.description || "";
  const detail = props.detail || "";
  const buttons = props.buttons || [];
  const featuredMedia = props.featuredMedia || null;
  const backgroundMedia = props.backgroundMedia || null;
  const backgroundColor = props.backgroundColor || "";
  const layout = props.layout || "text-left";
  const contentAlign = props.contentAlign || "left";

  // Update a specific prop
  const updateProp = (key, value) => {
    editor.updateBlock(block, {
      props: {
        ...props,
        [key]: value,
      },
    });
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  // Render collapsible section header
  const renderSectionHeader = (title, sectionKey, required = false) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionKey)}
      className="flex items-center justify-between w-full p-3 bg-gray-100 hover:bg-gray-200 rounded-t transition-colors"
    >
      <span className="font-medium text-neutral-900">
        {title}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      <svg
        className={`w-5 h-5 text-neutral-600 transition-transform ${
          expandedSections[sectionKey] ? "transform rotate-180" : ""
        }`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
  );

  // Edit Mode UI
  if (isEditMode) {
    return (
      <div className="hero-section-block w-full border-2 border-blue-200 rounded-lg p-4 bg-white my-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b">
          <div className="flex items-center gap-2">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              />
            </svg>
            <span className="font-semibold text-neutral-900">Hero Section</span>
          </div>
          <button
            type="button"
            onClick={() => setIsEditMode(false)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Preview
          </button>
        </div>

        {/* Content Section */}
        <div className="mb-4">
          {renderSectionHeader("Content", "content", true)}
          {expandedSections.content && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4 space-y-3">
              {/* Small Title */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Small Title{" "}
                  <span className="text-neutral-500 text-xs">(optional)</span>
                </label>
                <input
                  type="text"
                  value={smallTitle}
                  onChange={(e) => updateProp("smallTitle", e.target.value)}
                  placeholder="e.g., NEW PRODUCT"
                  className="form-control"
                  maxLength={100}
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Main Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => updateProp("title", e.target.value)}
                  placeholder="Enter your headline"
                  className="form-control"
                  maxLength={200}
                  required
                />
                <div className="text-xs text-neutral-500 mt-1">
                  {title.length}/200
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => updateProp("description", e.target.value)}
                  placeholder="Enter supporting text"
                  className="form-control"
                  rows={3}
                  maxLength={500}
                  required
                />
                <div className="text-xs text-neutral-500 mt-1">
                  {description.length}/500
                </div>
              </div>

              {/* Detail */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Extended Details{" "}
                  <span className="text-neutral-500 text-xs">(optional)</span>
                </label>
                <textarea
                  value={detail}
                  onChange={(e) => updateProp("detail", e.target.value)}
                  placeholder="Additional information..."
                  className="form-control"
                  rows={4}
                />
              </div>

              {/* Text Alignment */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Text Alignment
                </label>
                <div className="flex gap-2">
                  {["left", "center", "right"].map((align) => (
                    <button
                      key={align}
                      type="button"
                      onClick={() => updateProp("contentAlign", align)}
                      className={`flex-1 py-2 px-3 rounded border text-sm font-medium transition-colors ${
                        contentAlign === align
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-neutral-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {align.charAt(0).toUpperCase() + align.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <ButtonEditor
                buttons={buttons}
                onChange={(newButtons) => updateProp("buttons", newButtons)}
                maxButtons={2}
              />
            </div>
          )}
        </div>

        {/* Featured Media Section */}
        <div className="mb-4">
          {renderSectionHeader("Featured Media", "media")}
          {expandedSections.media && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4">
              <MediaSelector
                value={featuredMedia}
                onChange={(media) => updateProp("featuredMedia", media)}
                label="Featured Image, Video, SVG, or Animation"
                allowVideo={true}
                allowYoutube={true}
                allowSvg={true}
                allowAnimation={true}
              />
            </div>
          )}
        </div>

        {/* Background Media Section */}
        <div className="mb-4">
          {renderSectionHeader("Background (Optional)", "background")}
          {expandedSections.background && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4 space-y-4">
              {/* Background Color */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Background Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={backgroundColor || "#ffffff"}
                    onChange={(e) =>
                      updateProp("backgroundColor", e.target.value)
                    }
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) =>
                      updateProp("backgroundColor", e.target.value)
                    }
                    placeholder="#RRGGBB or leave empty"
                    className="form-control flex-1"
                  />
                  {backgroundColor && (
                    <button
                      type="button"
                      onClick={() => updateProp("backgroundColor", "")}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Used when no background image/video is set
                </p>
              </div>

              <MediaSelector
                value={backgroundMedia}
                onChange={(media) => updateProp("backgroundMedia", media)}
                label="Background Image or Video"
                allowVideo={true}
                allowYoutube={false}
              />

              {backgroundMedia && backgroundMedia.url && (
                <OverlayControls
                  value={backgroundMedia.overlay || {}}
                  onChange={(overlay) =>
                    updateProp("backgroundMedia", {
                      ...backgroundMedia,
                      overlay,
                    })
                  }
                  showPreview={true}
                />
              )}
            </div>
          )}
        </div>

        {/* Layout Section */}
        <div className="mb-4">
          {renderSectionHeader("Layout", "layout")}
          {expandedSections.layout && (
            <div className="border border-t-0 border-gray-200 rounded-b p-4 space-y-4">
              <LayoutSelector
                value={layout}
                onChange={(newLayout) => updateProp("layout", newLayout)}
                layouts={LAYOUT_OPTIONS}
                blockType="hero"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // View Mode (Preview) - Basic rendering
  // Note: Full responsive SSR rendering is handled by HeroSectionComponent
  return (
    <div className="hero-section-block border border-gray-300 rounded-lg p-6 bg-white my-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-neutral-600">
          Hero Section Preview
        </span>
        <button
          type="button"
          onClick={() => setIsEditMode(true)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Edit
        </button>
      </div>

      <div className="space-y-4">
        {smallTitle && (
          <div className="text-sm font-semibold text-blue-600 uppercase tracking-wide">
            {smallTitle}
          </div>
        )}

        {title && (
          <h2 className="text-3xl font-bold text-neutral-900">{title}</h2>
        )}

        {description && (
          <p className="text-lg text-neutral-600">{description}</p>
        )}

        {detail && <p className="text-neutral-700">{detail}</p>}

        {buttons && buttons.length > 0 && (
          <ButtonEditor
            buttons={buttons}
            onChange={() => {}}
            isEditMode={false}
          />
        )}

        {featuredMedia && featuredMedia.url && (
          <div className="mt-4">
            {featuredMedia.type === "image" ? (
              <img
                src={featuredMedia.url}
                alt={featuredMedia.alt || ""}
                className="max-w-full h-auto rounded"
              />
            ) : (
              <div className="text-sm text-neutral-500 p-4 border rounded">
                Video: {featuredMedia.url}
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-neutral-500 mt-4 pt-4 border-t">
          Layout: {layout} • SSR rendering will apply full responsive styles
        </div>
      </div>
    </div>
  );
}
